import React from 'react';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Skeleton from '../../components/ui/Skeleton/Skeleton';
import Tooltip from '../../components/ui/Tooltip/Tooltip';
import { AlertTriangleIcon, MailIcon, InboxIcon } from '../../components/ui/Icons';
import {
  NotificationChannel,
  NotificationSettings as Settings,
  getNotificationSettings,
  getUserNotificationSettings,
  saveNotificationSettings,
  saveUserNotificationSettings,
} from '../../notifications/api';
import DeliveryHours from './DeliveryHours';
import { FEATURE_MULTIMODAL } from '../../featureFlags';
import styles from './NotificationSettings.module.css';

/**
 * Avisos que solo existen por marítimo y aéreo: el resumen de la corrida que los rastrea.
 *
 * 🔄 **Se esconden sin `FEATURE_MULTIMODAL`** (14-oct-2026), igual que esos módulos: con la bandera apagada el servidor
 * tampoco los manda (`config/features` del API), así que la casilla no produciría nada. Los retrasos y los documentos
 * faltantes se quedan: también los hay de cargas.
 */
const MULTIMODAL_ONLY_EVENTS = ['job_summary'];

/**
 * PREFERENCIAS DE NOTIFICACIÓN — la tercera sección de "Mi perfil".
 *
 * ## 🔑 La lista de avisos viene del servidor
 *
 * No hay ningún tipo de evento escrito en este archivo. El backend manda el catálogo junto con las
 * preferencias, así que agregar un aviso es tocar `constants/notifications.ts` y la casilla aparece sola. Una
 * lista escrita a mano aquí se queda corta el día que se agregue un aviso, y nadie se enteraría — la pantalla
 * simplemente no lo ofrecería.
 *
 * ## ⚠️ Lo que esta pantalla tiene que dejar claro y no es obvio
 *
 * Que marcar la casilla de **correo** no sirve de nada si la dirección no está verificada. Es el reporte más
 * difícil de diagnosticar de todo el módulo: la persona prende el aviso, no le llega nada, y desde afuera
 * todo parece correcto. Por eso el aviso de "verifica tu correo" aparece **junto a la columna** de correo y
 * no en una nota al pie.
 *
 * ## 🔄 Desde el 29-sep la columna de correo se BLOQUEA sin verificar
 *
 * Lo pidió el usuario: *"estará desactivada la parte de correo con un tooltip que haga mención que solo se activará
 * si se verifica el correo"*. Antes la casilla se podía prender y el aviso de arriba explicaba que no llegaría nada;
 * ahora no se puede prender, y el backend rechaza el guardado si alguien lo intenta a mano.
 *
 * ⚠️ El interruptor bloqueado usa `aria-disabled` y no `disabled`: un botón `disabled` sale del orden de tabulación y
 * en varios navegadores no dispara los eventos del ratón, así que ni el teclado ni el ratón llegarían a la explicación.
 *
 * 📌 Y todo arranca **apagado** (decisión del mismo día): quien entra por primera vez no recibe nada hasta que elige.
 */

/** Un cambio pendiente de guardar. La clave es `evento|canal`. */
type Draft = Record<string, boolean>;

const key = (eventType: string, channel: NotificationChannel) => `${eventType}|${channel}`;

interface NotificationSettingsProps {
  /**
   * Id del usuario a editar. **Sin él, el formulario edita al de la sesión**, que es el caso de "Mi perfil".
   *
   * 🔑 **Se reutiliza el formulario en lugar de duplicarlo** (mismo criterio que `PortalProvidersCard` en
   * Auditoría): una segunda copia de esta tabla se desincroniza el día que se agregue un canal, y el síntoma
   * sería que la pantalla del administrador ofrece menos opciones que la del propio usuario — sin que nada falle.
   *
   * ⚠️ Lo que **no** se comparte es la ruta: ver la nota de `getUserNotificationSettings`.
   */
  userId?: string;
  /** Nombre o correo del sujeto, para que el encabezado diga de quién son estas preferencias. */
  userLabel?: string;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ userId, userLabel }) => {
  /**
   * 🔴 **Editar a otra persona tiene que VERSE distinto.** Con el mismo encabezado y el mismo texto, un
   * administrador que llega desde el detalle de un usuario no tiene ninguna señal de que las casillas que está
   * moviendo no son las suyas — y el error solo se descubre cuando deja de recibir sus propios avisos.
   */
  const ajeno = Boolean(userId);

  /** El tooltip de la columna de correo bloqueada: *"solo se activará si se verifica el correo"*. */
  const lockedHint = ajeno
    ? 'Email turns on once this user verifies their address.'
    : 'Email turns on once your address is verified.';

  const [settings, setSettings] = React.useState<Settings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{ ok: boolean; text: string } | null>(null);

  /**
   * Cambios locales sobre lo que llegó del servidor.
   *
   * 🔑 Se guarda **solo lo que se movió**, no una copia completa del estado. Así el botón de guardar puede
   * saber si hay algo que mandar, y —más importante— dos personas editando en pestañas distintas no se pisan
   * los avisos que ninguna de las dos tocó.
   */
  const [draft, setDraft] = React.useState<Draft>({});

  /** Id del aviso de "sin verificar": los interruptores bloqueados lo citan con `aria-describedby`. */
  const lockNoteId = React.useId();

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = userId ? await getUserNotificationSettings(userId) : await getNotificationSettings();
      setSettings(data);
      setDraft({});
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          (userId
            ? 'Could not load the notification settings for that user'
            : 'Could not load your notification settings')
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  /**
   * 🔑 El correo está bloqueado mientras la dirección no se pueda usar. `emailReady` lo calcula el backend con la misma
   * regla que decide si un correo sale (`canReceiveEmail`), así que la pantalla no la repite.
   */
  const locked = (channel: NotificationChannel): boolean =>
    channel === 'email' && Boolean(settings) && !settings?.emailReady;

  const valueOf = (eventType: string, channel: NotificationChannel): boolean => {
    /**
     * 📌 Bloqueado se pinta **apagado** aunque haya una fila encendida de antes: es lo que pasa de verdad, porque sin
     * dirección verificada no sale ningún correo. La fila se conserva y vuelve a verse al verificar.
     */
    if (locked(channel)) return false;
    const k = key(eventType, channel);
    if (k in draft) return draft[k];
    const found = settings?.preferences.find(
      (p) => p.eventType === eventType && p.channel === channel
    );
    return found?.enabled ?? false;
  };

  const toggle = (eventType: string, channel: NotificationChannel) => {
    if (locked(channel)) return;
    const k = key(eventType, channel);
    const next = !valueOf(eventType, channel);
    setFeedback(null);
    setDraft((d) => {
      const copy = { ...d, [k]: next };
      /**
       * 📌 Si el valor vuelve a coincidir con lo que hay en el servidor, el cambio sale del borrador. Es lo
       * que hace que el botón se apague al deshacer un clic — sin esto, marcar y desmarcar dejaría el
       * formulario "sucio" para siempre y el usuario guardaría algo que no cambió.
       */
      const server = settings?.preferences.find(
        (p) => p.eventType === eventType && p.channel === channel
      );
      if (server && server.enabled === next) delete copy[k];
      return copy;
    });
  };

  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setFeedback(null);
    try {
      const items = Object.entries(draft).map(([k, enabled]) => {
        const [eventType, channel] = k.split('|');
        return { eventType, channel: channel as NotificationChannel, enabled };
      });
      const preferences = userId
        ? await saveUserNotificationSettings(userId, items)
        : await saveNotificationSettings(items);
      setSettings((s) => (s ? { ...s, preferences } : s));
      setDraft({});
      /** 📌 Se nombra al sujeto al confirmar: un "Saved." suelto no dice a quién se le guardó. */
      setFeedback({ ok: true, text: ajeno ? `Saved for ${userLabel || 'that user'}.` : 'Saved.' });
    } catch (err: any) {
      setFeedback({
        ok: false,
        text: err?.response?.data?.message || 'Could not save the settings',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <Skeleton height={200} radius={12} />
      </Card>
    );
  }

  if (error || !settings) {
    return (
      <Card>
        <h2 className={styles.cardTitle}>Notifications</h2>
        <div className={[styles.notice, styles.noticeError].join(' ')}>
          <AlertTriangleIcon size={17} />
          <span>{error || 'Could not load your notification settings'}</span>
        </div>
      </Card>
    );
  }

  return (
    <>
    <Card className={saving ? 'dylo-loading-host' : undefined}>
      {saving && <span className="dylo-loading-sweep" />}
      <h2 className={styles.cardTitle}>
        {ajeno ? 'Notifications for this user' : 'Notifications'}
      </h2>
      <p className={styles.cardHint}>
        {ajeno ? (
          <>
            {/**
             * 🔴 Se dice **de quién** son y que el cambio es sobre otra persona. Es la señal que evita que un
             * administrador mueva las casillas creyendo que son las suyas.
             */}
            What <strong>{userLabel || 'this user'}</strong> gets notified about. You are changing someone
            else's settings, and the change is recorded with your name.
          </>
        ) : (
          <>
            {/**
             * 📌 "Everything starts off" va primero: desde el 29-sep nada llega hasta que la persona lo prende, y sin
             * decirlo una bandeja vacía se lee como que la plataforma no funciona.
             */}
            Everything starts off. Turn on what you want to hear about, and where: in-platform notifications
            show up in the bell, and email goes to your verified address.
          </>
        )}
      </p>

      {/**
       * ⚠️ Va **arriba de la tabla** y no al pie: si estuviera abajo, alguien marcaría la columna de correo,
       * guardaría y se iría sin haberlo leído. Aquí lo lee antes de decidir.
       */}
      {!settings.emailReady && (
        <div className={[styles.notice, styles.noticeWarn].join(' ')}>
          <AlertTriangleIcon size={17} />
          <span id={lockNoteId}>
            {ajeno ? (
              <>
                {/**
                 * 🔑 El mismo hecho, pero la acción es de otra persona: un administrador **no puede** verificar
                 * el correo de alguien más. Decirle "usa la tarjeta de arriba" lo mandaría a un botón que no
                 * existe en esta pantalla.
                 */}
                This user's address isn't verified, so email notifications can't be turned on for them. Only
                they can confirm it, from their own profile.
              </>
            ) : (
              <>
                {/**
                 * 🔄 Antes decía que la casilla se guardaba igual aunque no llegara nada. Desde el 29-sep la columna
                 * se bloquea, así que el texto dice qué hacer para desbloquearla.
                 */}
                {/**
                 * 🔄 14-oct-2026: la verificación ya no es una tarjeta aparte; vive junto al nombre, en *My profile*,
                 * arriba de esta tabla.
                 */}
                Email notifications stay off until your address is verified. Confirm it next to your name in{' '}
                <strong>My profile</strong>, at the top of this page, and the email column turns on.
              </>
            )}
          </span>
        </div>
      )}

      <div className={styles.table}>
        <div className={styles.headRow}>
          <span className={styles.headEvent}>What happened</span>
          <span className={styles.headChannel} title="Shows up in the bell">
            <InboxIcon size={14} /> In-platform
          </span>
          <span
            className={[styles.headChannel, !settings.emailReady ? styles.headMuted : '']
              .filter(Boolean)
              .join(' ')}
          >
            <Tooltip content={settings.emailReady ? 'Goes to the verified address' : lockedHint} multiline>
              <span className={styles.headLabel}>
                <MailIcon size={14} /> Email
              </span>
            </Tooltip>
          </span>
        </div>

        {settings.events
          .filter((event) => FEATURE_MULTIMODAL || !MULTIMODAL_ONLY_EVENTS.includes(event.type))
          .map((event) => (
          <div key={event.type} className={styles.row}>
            <span className={styles.eventCell}>
              <span className={styles.eventLabel}>
                {event.label}
                {/**
                 * 📌 La marca de "supervisión" agrupa, **no esconde**. Un aviso de retraso puede interesarle
                 * a un cliente algún día; el acotado real lo hace el acceso al embarque, y confundir "esto es
                 * de administradores" con "esto no se le puede mostrar a nadie más" es cómo se construye un
                 * filtro que después hay que desmontar.
                 */}
                {event.supervisory && <span className={styles.tag}>supervision</span>}
              </span>
              <span className={styles.eventDesc}>{event.description}</span>
            </span>

            {(['platform', 'email'] as NotificationChannel[]).map((channel) => {
              const on = valueOf(event.type, channel);
              const changed = key(event.type, channel) in draft;
              const isLocked = locked(channel);
              const button = (
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`${event.label}: ${channel === 'email' ? 'email' : 'in-platform'}`}
                  aria-disabled={isLocked || undefined}
                  aria-describedby={isLocked ? lockNoteId : undefined}
                  className={[
                    styles.switch,
                    on ? styles.switchOn : '',
                    changed ? styles.switchDirty : '',
                    isLocked ? styles.switchLocked : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => toggle(event.type, channel)}
                >
                  <span className={styles.knob} />
                </button>
              );
              return (
                <span key={channel} className={styles.switchCell}>
                  {isLocked ? (
                    <Tooltip content={lockedHint} multiline>
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  )}
                </span>
              );
            })}
          </div>
        ))}
      </div>

      {feedback && (
        <div
          className={[styles.notice, feedback.ok ? styles.noticeOk : styles.noticeError].join(' ')}
          role="status"
        >
          <AlertTriangleIcon size={17} />
          <span>{feedback.text}</span>
        </div>
      )}

      <div className={styles.actions}>
        {/**
         * 📌 El botón dice **cuántos** cambios va a guardar. Es un detalle chico que evita el caso de marcar
         * algo sin querer y guardarlo sin darse cuenta: "Save 1 change" contra "Save 4 changes" es una
         * diferencia que se nota antes de pulsar.
         */}
        <span className={styles.dirtyHint}>
          {dirty
            ? `${Object.keys(draft).length} change${Object.keys(draft).length === 1 ? '' : 's'} not saved`
            : 'Everything saved'}
        </span>
        <Button type="button" disabled={!dirty} loading={saving} onClick={save}>
          Save changes
        </Button>
      </div>
    </Card>

    {/**
     * 🔑 **Cuándo llegan, justo debajo de qué llega** (09-oct-2026). Antes la única forma de pedir avisos a ciertas
     * horas era embarque por embarque; ahora es una decisión de cada persona, y vive junto a las demás.
     *
     * 📌 Se esconde si el backend no manda `schedule` (uno anterior al cambio): ofrecer horas que no se pueden guardar
     * sería peor que no ofrecerlas. La `key` vuelve a montar la sección al cambiar de usuario, para que no se quede
     * con las horas del anterior.
     */}
    {settings.schedule && (
      <DeliveryHours
        key={userId || 'me'}
        initial={settings.schedule}
        userId={userId}
        userLabel={userLabel}
      />
    )}
    </>
  );
};

export default NotificationSettings;
